import styled from '@emotion/styled';
import { Tabs as BaseTabs } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { css } from 'config/emotionCss';
import Dropdown from 'ui/base/Dropdown';

/** @typedef {NonNullable<import('antd').TabsProps['items']>[number] & { hide?: boolean, 'data-cy'?: string }} TabsItemProps */
/** @typedef { import('antd').TabsProps & { items?: TabsItemProps[] } } FBTabsProps */
/** @type {React.FC<FBTabsProps>} */
const FBTabs = ({ items: baseItems, children, ...tabsProps }) => {
  const items = baseItems?.reduce((acc, { hide, 'data-cy': dataCy, label, ...item }) => {
    if (!hide) {
      acc.push({
        ...item,
        label: dataCy ? <span data-cy={dataCy}>{label}</span> : label,
      });
    }
    return acc;
  }, []);

  return (
    <BaseTabs {...tabsProps} items={items}>
      {children}
    </BaseTabs>
  );
};

export default FBTabs;

export const StyledLanguageTabs = styled(FBTabs)`
  .ant-tabs-tab {
    padding: 0 !important;

    > * {
      line-height: 40px;
      padding: 0 16px;
    }

    &[data-node-key='add'] {
      > .ant-tabs-tab-btn {
        padding: 0;
        line-height: 1;

        [class*='fb-icon'] {
          width: 46px;
          height: 38px;
          font-size: 14px;
          line-height: 36px;
          text-align: center;
          display: inline-block;

          & + span { display : none }
        }
      }
    }
  }
`;

const addTabOverlayStyle = css`
  > ul {
    max-height : 256px;
    overflow-y: auto;
  }
`;

/** @type {React.FC<{ disabled: boolean, languagesToDisplay: string[], handleClick: () => void, }>} */
export const AddTab = ({ disabled: disabledProp, languagesToDisplay, handleClick }) => {
  const disabled = disabledProp || languagesToDisplay.length === 0;

  /** @type {import("antd").MenuProps} */
  const menu = React.useMemo(() => ({
    onClick: handleClick,
    items: languagesToDisplay.map(lang => ({
      key: lang,
      'data-cy': `addLang-${lang}`,
      label: <FormattedMessage id={`language.${lang}`} defaultMessage={lang} />,
    })),
  }), [handleClick, languagesToDisplay]);

  return (
    <Dropdown
      menu={menu}
      disabled={disabled}
      trigger={['click']}
      data-cy="add-language"
      classNames={{ root: addTabOverlayStyle }}
    >
      <div className="d-flex align-items-center justify-content-center">
        <i className="fb-icon--add" data-cy="addLang" />
      </div>
    </Dropdown>
  );
};
